import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { advertiserRequests, categories, locations, budgetRanges, statusFilters } from '@/mocks/adMarketplace';
import type { AdvertiserRequest, RequestStatus, BudgetRange } from '@/mocks/adMarketplace';

type LoadState = 'loading' | 'ready' | 'error';

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 350 + Math.random() * 200;
    setTimeout(() => {
      if (Math.random() > 0.97) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

function simulateSubmit(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), 700 + Math.random() * 500);
  });
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function formatCurrencyFull(n: number): string {
  return `$${n.toLocaleString('es-AR')}`;
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 animate-pulse">
        <div className="w-40 h-4 bg-background-200 rounded mb-3"></div>
        <div className="w-28 h-8 bg-background-200 rounded mb-4"></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 bg-background-200 rounded-xl"></div>
          ))}
        </div>
      </div>
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 animate-pulse">
        <div className="flex gap-2 mb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-24 h-8 bg-background-200 rounded-lg"></div>
          ))}
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 bg-background-200 rounded-xl mb-2"></div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-rose-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Status Badge ── */
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; bg: string; label: string; icon: string }> = {
    nueva: { color: 'text-sky-600', bg: 'bg-sky-50', label: 'Nueva', icon: 'ri-sparkling-line' },
    analizando: { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Analizando', icon: 'ri-search-eye-line' },
    'propuesta-enviada': { color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Propuesta enviada', icon: 'ri-send-plane-line' },
    cerrada: { color: 'text-foreground-400', bg: 'bg-background-200', label: 'Cerrada', icon: 'ri-lock-line' },
  };
  const s = map[status] || map.nueva;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${s.bg} ${s.color} text-[10px] font-medium whitespace-nowrap`}>
      <i className={`${s.icon} text-[10px]`}></i>
      {s.label}
    </span>
  );
}

/* ── Category Badge ── */
function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[10px] font-medium whitespace-nowrap">
      {category}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function AdminMarketplacePage() {
  const [requests, setRequests] = useState<AdvertiserRequest[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [filterCategory, setFilterCategory] = useState('Todas');
  const [filterLocation, setFilterLocation] = useState('Todas');
  const [filterBudget, setFilterBudget] = useState<BudgetRange>('todos');
  const [filterStatus, setFilterStatus] = useState<RequestStatus>('todas');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(() => {
    setLoadState('loading');
    setErrorMsg(null);
    simulateLoad(advertiserRequests)
      .then((d) => {
        setRequests(d);
        setLoadState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Filtering ── */
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      if (filterCategory !== 'Todas' && r.category !== filterCategory) return false;
      if (filterLocation !== 'Todas' && r.location !== filterLocation) return false;
      if (filterStatus !== 'todas' && r.status !== filterStatus) return false;
      if (filterBudget !== 'todos') {
        const b = r.budget;
        switch (filterBudget) {
          case 'menos-500': if (b >= 500) return false; break;
          case '500-1500': if (b < 500 || b > 1500) return false; break;
          case '1500-3000': if (b < 1500 || b > 3000) return false; break;
          case 'mas-3000': if (b <= 3000) return false; break;
        }
      }
      return true;
    });
  }, [requests, filterCategory, filterLocation, filterBudget, filterStatus]);

  /* ── Stats ── */
  const newCount = requests.filter((r) => r.status === 'nueva').length;
  const totalBudget = requests.reduce((sum, r) => sum + r.budget, 0);

  /* ── View detail ── */
  const handleViewDetail = (item: AdvertiserRequest) => {
    Swal.fire({
      title: item.companyName,
      html: `
        <div style="text-align:left;font-size:12px;line-height:2;font-family:inherit;">
          <div style="margin-bottom:8px;">
            ${item.status === 'nueva' ? '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;background:#e0f2fe;color:#0284c7;font-size:10px;font-weight:600;"><i class="ri-sparkling-line" style="font-size:10px;"></i> Nueva</span>' : ''}
            ${item.status === 'analizando' ? '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;background:#fef3c7;color:#d97706;font-size:10px;font-weight:600;"><i class="ri-search-eye-line" style="font-size:10px;"></i> Analizando</span>' : ''}
            ${item.status === 'propuesta-enviada' ? '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;background:#d1fae5;color:#059669;font-size:10px;font-weight:600;"><i class="ri-send-plane-line" style="font-size:10px;"></i> Propuesta enviada</span>' : ''}
            ${item.status === 'cerrada' ? '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;background:#e5e7eb;color:#6b7280;font-size:10px;font-weight:600;"><i class="ri-lock-line" style="font-size:10px;"></i> Cerrada</span>' : ''}
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:99px;background:#f3f4f6;color:#6b7280;font-size:10px;font-weight:500;margin-left:4px;">${item.category}</span>
          </div>
          <p style="color:#374151;margin:8px 0;">${item.description}</p>
          <hr style="border:none;border-top:1px solid #f3f4f6;margin:12px 0;" />
          <p><strong style="color:#111827;">💰 Presupuesto:</strong> <span style="color:#059669;font-weight:700;">$${item.budget.toLocaleString()} / ${item.budgetType}</span></p>
          <p><strong style="color:#111827;">📍 Ubicación:</strong> ${item.location}</p>
          <p><strong style="color:#111827;">🎯 Audiencia:</strong> ${item.targetAudience}</p>
          <p><strong style="color:#111827;">📻 Formato preferido:</strong> ${item.preferredFormat}</p>
          <p><strong style="color:#111827;">📅 Duración:</strong> ${item.duration} · Inicio: ${item.startDate}</p>
          <p><strong style="color:#111827;">📋 Publicado:</strong> ${item.postedDate}</p>
          <p><strong style="color:#111827;">📊 Propuestas recibidas:</strong> ${item.proposals}</p>
          <hr style="border:none;border-top:1px solid #f3f4f6;margin:12px 0;" />
          <p><strong style="color:#111827;">📧 Contacto:</strong> ${item.contactEmail}</p>
          <p><strong style="color:#111827;">📞 Teléfono:</strong> ${item.contactPhone}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '<i class="ri-send-plane-line"></i> Enviar propuesta',
      cancelButtonText: 'Cerrar',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl text-sm max-w-lg',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-bold px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleSendProposal(item);
      }
    });
  };

  /* ── Send proposal ── */
  const handleSendProposal = (item: AdvertiserRequest) => {
    Swal.fire({
      title: `Enviar propuesta a ${item.companyName}`,
      html: `
        <div style="text-align:left;font-size:12px;">
          <p style="color:#6b7280;margin-bottom:12px;font-size:11px;">Armá tu propuesta para <strong>${item.advertiserName}</strong> · Presupuesto de referencia: <strong style="color:#059669;">$${item.budget.toLocaleString()}/${item.budgetType}</strong></p>
          <label style="display:block;font-weight:600;margin-bottom:4px;color:#374151;font-size:11px;">Monto ofertado (${item.budgetType})</label>
          <input id="swal-proposal-amount" type="number" value="${item.budget}" class="swal2-input" style="width:100%;margin-bottom:12px;font-size:12px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:12px;" placeholder="Monto en ARS">

          <label style="display:block;font-weight:600;margin-bottom:4px;color:#374151;font-size:11px;">Tipo de espacio ofrecido</label>
          <select id="swal-proposal-format" class="swal2-input" style="width:100%;margin-bottom:12px;font-size:12px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:12px;">
            <option value="Menciones en vivo">Menciones en vivo</option>
            <option value="Segmento sponsoreado">Segmento sponsoreado</option>
            <option value="Programa completo">Programa completo</option>
            <option value="Banners web + Menciones">Banners web + Menciones</option>
            <option value="Eventos + Menciones">Eventos + Menciones</option>
            <option value="Concursos + Sorteos">Concursos + Sorteos</option>
          </select>

          <label style="display:block;font-weight:600;margin-bottom:4px;color:#374151;font-size:11px;">Duración propuesta</label>
          <select id="swal-proposal-duration" class="swal2-input" style="width:100%;margin-bottom:12px;font-size:12px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:12px;">
            <option value="1 mes">1 mes</option>
            <option value="3 meses" selected>3 meses</option>
            <option value="6 meses">6 meses</option>
            <option value="12 meses">12 meses</option>
          </select>

          <label style="display:block;font-weight:600;margin-bottom:4px;color:#374151;font-size:11px;">Mensaje (máx. 500 caracteres)</label>
          <textarea id="swal-proposal-message" maxlength="500" class="swal2-textarea" style="width:100%;height:100px;font-size:12px;padding:10px 12px;border:1px solid #e5e7eb;border-radius:12px;resize:none;" placeholder="Contale por qué tu radio es la mejor opción para su marca..."></textarea>
          <p style="font-size:10px;color:#9ca3af;margin-top:4px;" id="swal-proposal-charcount">0/500</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Enviar propuesta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl text-sm max-w-lg',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-bold px-4 py-2 rounded-xl',
        cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
      didOpen: () => {
        const textarea = document.getElementById('swal-proposal-message') as HTMLTextAreaElement;
        const charcount = document.getElementById('swal-proposal-charcount');
        if (textarea && charcount) {
          textarea.addEventListener('input', () => {
            charcount.textContent = `${textarea.value.length}/500`;
          });
        }
      },
      preConfirm: () => {
        const amount = (document.getElementById('swal-proposal-amount') as HTMLInputElement)?.value;
        const message = (document.getElementById('swal-proposal-message') as HTMLTextAreaElement)?.value || '';

        if (!amount || Number(amount) <= 0) {
          Swal.showValidationMessage('Ingresá un monto válido');
          return false;
        }
        return { amount: Number(amount), message };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setSubmitting(true);
        simulateSubmit().then(() => {
          setSubmitting(false);
          setRequests((prev) =>
            prev.map((r) =>
              r.id === item.id
                ? { ...r, status: 'propuesta-enviada', proposals: r.proposals + 1 }
                : r
            )
          );
          Swal.fire({
            title: 'Propuesta enviada',
            text: `Tu propuesta de $${result.value?.amount?.toLocaleString()} fue enviada a ${item.companyName}.`,
            icon: 'success',
            timer: 2800,
            showConfirmButton: false,
            customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
          });
        }).catch(() => {
          setSubmitting(false);
          Swal.fire({
            title: 'Error',
            text: 'No se pudo enviar la propuesta.',
            icon: 'error',
            customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
          });
        });
      }
    });
  };

  /* ── Loading ── */
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
              <div className="w-32 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-44 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8">
              <LoadingSkeleton />
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (loadState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center px-4 h-14">
            <div className="flex items-center gap-2.5 pl-12">
              <div className="w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center">
                <i className="ri-error-warning-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Marketplace</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <ErrorState message={errorMsg || 'Error desconocido'} onRetry={loadData} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Marketplace</h1>
              <p className="text-foreground-400 text-[9px]">{requests.length} solicitudes · {formatCurrency(totalBudget)} total</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Marketplace de Anunciantes</h2>
              <p className="text-foreground-400 text-xs">
                {requests.length} solicitudes activas · {newCount} nuevas sin propuesta · Objetivo: <strong className="text-emerald-600">conseguir anunciantes</strong>
              </p>
            </div>
            <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-external-link-line text-xs"></i>
              Ver sitio
            </Link>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* ── Hero ── */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0">
                      <i className="ri-store-2-line text-white text-xl"></i>
                    </div>
                    <div>
                      <p className="text-emerald-700 text-xs font-bold">Marketplace</p>
                      <p className="text-foreground-800 text-2xl md:text-3xl font-black">{requests.length} solicitudes</p>
                    </div>
                  </div>
                  <p className="text-foreground-400 text-xs mt-1 max-w-lg">
                    Anunciantes buscando pauta publicitaria en radios como la tuya. 
                    Revisá las solicitudes, filtrá por lo que te interesa y <strong className="text-foreground-700">enviá tu propuesta</strong> para sumarlos como anunciantes.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 lg:w-[400px] shrink-0">
                  <div className="bg-sky-50 rounded-xl p-3 text-center">
                    <p className="text-sky-700 text-lg font-black">{newCount}</p>
                    <p className="text-sky-600 text-[9px] font-medium">Nuevas</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 text-center">
                    <p className="text-amber-700 text-lg font-black">{requests.filter((r) => r.status === 'analizando').length}</p>
                    <p className="text-amber-600 text-[9px] font-medium">Analizando</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <p className="text-emerald-700 text-lg font-black">{formatCurrency(totalBudget)}</p>
                    <p className="text-emerald-600 text-[9px] font-medium">Presupuesto total</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Filters ── */}
            <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Status */}
                  <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
                    {statusFilters.map((sf) => (
                      <button
                        key={sf.value}
                        onClick={() => setFilterStatus(sf.value)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          filterStatus === sf.value
                            ? 'bg-emerald-500 text-white'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {sf.label}
                      </button>
                    ))}
                  </div>
                  <span className="w-px h-5 bg-background-300 hidden sm:block"></span>
                  {/* Category */}
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="text-[10px] font-semibold px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 cursor-pointer"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c === 'Todas' ? 'Todas las categorías' : c}</option>
                    ))}
                  </select>
                  {/* Location */}
                  <select
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                    className="text-[10px] font-semibold px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 cursor-pointer"
                  >
                    {locations.map((l) => (
                      <option key={l} value={l}>{l === 'Todas' ? 'Todas las ubicaciones' : l}</option>
                    ))}
                  </select>
                  {/* Budget */}
                  <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
                    {budgetRanges.map((br) => (
                      <button
                        key={br.value}
                        onClick={() => setFilterBudget(br.value)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          filterBudget === br.value
                            ? 'bg-emerald-500 text-white'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {br.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Results count ── */}
              <div className="px-5 py-2 border-b border-background-200 flex items-center gap-2">
                <span className="text-foreground-400 text-[10px]">
                  <strong className="text-foreground-700">{filteredRequests.length}</strong> solicitudes encontradas
                </span>
              </div>

              {/* ── Desktop Table ── */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100/50">
                      <th className="text-left py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Anunciante</th>
                      <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Categoría</th>
                      <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Ubicación</th>
                      <th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Presupuesto</th>
                      <th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Formato</th>
                      <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Propuestas</th>
                      <th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                      <th className="text-center py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-background-200 hover:bg-background-50/50 transition-colors group cursor-pointer"
                        onClick={() => handleViewDetail(item)}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-foreground-800 text-xs font-semibold">{item.companyName}</p>
                            <p className="text-foreground-400 text-[10px]">{item.advertiserName}</p>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <CategoryBadge category={item.category} />
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-foreground-500 text-xs">{item.location}</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.budget)}</span>
                          <span className="text-foreground-400 text-[9px]">/{item.budgetType}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-foreground-500 text-[10px] max-w-[160px] line-clamp-1">{item.preferredFormat}</span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`text-xs font-medium ${item.proposals > 0 ? 'text-foreground-600' : 'text-foreground-300'}`}>
                            {item.proposals}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleViewDetail(item)}
                              className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold hover:bg-background-200 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-eye-line text-[10px] mr-1"></i>
                              Ver
                            </button>
                            {item.status !== 'cerrada' && (
                              <button
                                onClick={() => handleSendProposal(item)}
                                disabled={submitting}
                                className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[10px] font-semibold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                              >
                                <i className="ri-send-plane-line text-[10px] mr-1"></i>
                                Propuesta
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Mobile Cards ── */}
              <div className="lg:hidden divide-y divide-background-200">
                {filteredRequests.map((item) => (
                  <div key={item.id} className="p-4 cursor-pointer hover:bg-background-50/50 transition-colors" onClick={() => handleViewDetail(item)}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <p className="text-foreground-800 text-xs font-bold">{item.companyName}</p>
                        <p className="text-foreground-400 text-[10px]">{item.advertiserName}</p>
                      </div>
                      <StatusBadge status={item.status} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <CategoryBadge category={item.category} />
                      <span className="text-foreground-500 text-[10px]">{item.location}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-600 text-xs font-bold">{formatCurrency(item.budget)}/{item.budgetType}</span>
                        <span className="text-foreground-300 text-[10px]">·</span>
                        <span className="text-foreground-400 text-[10px]">{item.proposals} prop.</span>
                      </div>
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleViewDetail(item)}
                          className="px-2.5 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-eye-line text-[10px]"></i>
                        </button>
                        {item.status !== 'cerrada' && (
                          <button
                            onClick={() => handleSendProposal(item)}
                            disabled={submitting}
                            className="px-2.5 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-semibold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-send-plane-line text-[10px]"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty state */}
              {filteredRequests.length === 0 && (
                <div className="p-12 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-foreground-300 text-xl"></i>
                  </div>
                  <p className="text-foreground-500 text-xs font-semibold mb-1">Sin resultados</p>
                  <p className="text-foreground-400 text-[10px]">Probá ajustando los filtros para ver más solicitudes.</p>
                </div>
              )}
            </div>

            {/* ── CTA Footer ── */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0">
                  <i className="ri-user-add-line text-white text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-emerald-800 text-sm font-bold">Conseguí más anunciantes</h3>
                  <p className="text-emerald-700/80 text-xs mt-1">
                    Revisá el marketplace todos los días. Las solicitudes nuevas aparecen primero. 
                    Mientras más rápido envíes tu propuesta, más chances de cerrar el anunciante.
                  </p>
                </div>
                <div className="text-center shrink-0">
                  <span className="text-3xl font-black text-emerald-600">{newCount}</span>
                  <p className="text-emerald-600 text-[10px] font-bold">solicitudes nuevas</p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}